import type { GrafikDegeri, GrafikNoktasi, GrafikSerisi, GrafikTonu } from "./tipler";

export const TON_RENKLERI: Record<GrafikTonu, string> = {
  marka: "var(--color-marka-600)",
  vurgu: "var(--color-vurgu-600)",
  onay: "var(--color-basarili-500)",
  uyari: "var(--color-uyari-600)",
  hata: "var(--color-hata-500)",
  notr: "var(--color-notr-600)",
};

export function olculen(deger: GrafikDegeri, negatifOlabilir = false): number | null {
  return typeof deger === "number" && Number.isFinite(deger) && (negatifOlabilir || deger >= 0)
    ? deger
    : null;
}

export function sayiMetni(deger: GrafikDegeri, birim = ""): string {
  const sayi = olculen(deger, true);
  if (sayi === null) return "Ölçülmedi";
  const metin = new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 }).format(sayi);
  return birim === "%" ? `%${metin}` : `${metin}${birim ? ` ${birim}` : ""}`;
}

export function tarihAni(tarih: string | undefined): number | null {
  if (!tarih) return null;
  // Ofsetsiz anı cihazın saat diliminde yorumlamak günün kaymasına yol açar.
  const gun = /^\d{4}-\d{2}-\d{2}$/.test(tarih);
  if (!gun && !/(Z|[+-]\d{2}:\d{2})$/i.test(tarih)) return null;
  const an = Date.parse(gun ? `${tarih}T12:00:00+03:00` : tarih);
  if (!Number.isFinite(an)) return null;
  if (
    gun &&
    new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Istanbul" }).format(an) !== tarih
  ) {
    return null;
  }
  return an;
}

export function noktaEtiketi(nokta: Pick<GrafikNoktasi, "etiket" | "tarih">, sira: number): string {
  if (nokta.etiket?.trim()) return nokta.etiket;
  const an = tarihAni(nokta.tarih);
  return an === null
    ? nokta.tarih
      ? "Geçersiz tarih"
      : `${sira + 1}. kayıt`
    : new Intl.DateTimeFormat("tr-TR", {
        day: "numeric",
        month: "short",
        timeZone: "Europe/Istanbul",
      }).format(an);
}

export function kisalt(metin: string, sinir = 9): string {
  const harfler = Array.from(metin);
  return harfler.length > sinir ? `${harfler.slice(0, sinir - 1).join("")}…` : metin;
}

export function guvenliToplam(degerler: readonly number[]): number {
  return degerler.reduce((toplam, deger) => Math.min(Number.MAX_VALUE, toplam + deger), 0);
}

export function hedefDegeri(nokta: GrafikNoktasi, hedef: GrafikDegeri, negatifOlabilir = false) {
  // Açık null hedef "bu günün hedefi yok" demektir; ortak hedefle doldurulmaz.
  return olculen(nokta.hedef === undefined ? hedef : nokta.hedef, negatifOlabilir);
}

export function seriRengi(seri: GrafikSerisi, sira: number): string {
  return TON_RENKLERI[seri.ton ?? (sira % 2 === 0 ? "marka" : "vurgu")];
}

export function yOlcegi(degerler: readonly number[], ust: number, alt: number) {
  let enAz = 0;
  let enCok = 0;
  for (const deger of degerler) {
    enAz = Math.min(enAz, deger);
    enCok = Math.max(enCok, deger);
  }
  const bolen = Math.max(Math.abs(enAz), Math.abs(enCok), 1);
  const kucuk = enAz / bolen;
  const buyuk = enCok / bolen;
  // Çok büyük sonlu girdilerde ust-alt taşabileceği için önce normalize edilir.
  return {
    enAz,
    enCok,
    konum: (deger: number) => alt - ((deger / bolen - kucuk) / (buyuk - kucuk || 1)) * (alt - ust),
  };
}

export function zamanSirasi(noktalar: readonly GrafikNoktasi[]): readonly GrafikNoktasi[] {
  return noktalar.every((nokta) => tarihAni(nokta.tarih) !== null)
    ? [...noktalar].sort((sol, sag) => (tarihAni(sol.tarih) ?? 0) - (tarihAni(sag.tarih) ?? 0))
    : noktalar;
}

export function xKonumu(noktalar: readonly GrafikNoktasi[], sol: number, sag: number) {
  const anlar = noktalar.map((nokta) => tarihAni(nokta.tarih));
  const ilk = anlar[0];
  const son = anlar[anlar.length - 1];
  const zamanli = anlar.every((an) => an !== null) && ilk != null && son != null && son > ilk;
  return (sira: number) => {
    const an = anlar[sira];
    if (zamanli && an != null && ilk != null && son != null)
      return sol + ((an - ilk) / (son - ilk)) * (sag - sol);
    return noktalar.length <= 1
      ? (sol + sag) / 2
      : sol + (sira / (noktalar.length - 1)) * (sag - sol);
  };
}

export function zamanOzeti(
  baslik: string,
  noktalar: readonly GrafikNoktasi[],
  seriler: readonly GrafikSerisi[],
  birim: string,
  hedef: GrafikDegeri,
  negatifOlabilir: boolean,
  hedefYigili = !negatifOlabilir,
): string {
  const son = noktalar[noktalar.length - 1];
  const degerler = seriler
    .map(
      (seri) =>
        `${seri.etiket}: ${sayiMetni(olculen(son?.degerler[seri.anahtar], negatifOlabilir), birim)}`,
    )
    .join("; ");
  const hedefler = noktalar.map((nokta) => hedefDegeri(nokta, hedef, negatifOlabilir));
  const hedefli = hedefler.filter((deger): deger is number => deger !== null);
  const eksik = noktalar.filter((nokta) =>
    seriler.some((seri) => olculen(nokta.degerler[seri.anahtar], negatifOlabilir) === null),
  ).length;
  const ulasilan = noktalar.filter((nokta, sira) => {
    const hedefNoktasi = hedefler[sira];
    const olcumler = seriler.map((seri) => olculen(nokta.degerler[seri.anahtar], negatifOlabilir));
    const gerceklesen = negatifOlabilir
      ? olcumler[0]
      : guvenliToplam(olcumler.filter((deger): deger is number => deger !== null));
    return (
      hedefNoktasi != null &&
      gerceklesen != null &&
      olcumler.length > 0 &&
      olcumler.every((deger) => deger !== null) &&
      (!negatifOlabilir && !hedefYigili
        ? olcumler.every((deger) => deger !== null && deger >= hedefNoktasi)
        : gerceklesen >= hedefNoktasi)
    );
  }).length;
  return `${baslik}. ${noktalar.length} kayıt. Son kayıt: ${degerler || "Ölçülmedi"}.${
    hedefli.length
      ? ` ${hedefli.length} hedefli kaydın ${ulasilan} tanesinde hedef tamamlandı.`
      : ""
  }${eksik ? ` ${eksik} kayıtta eksik ölçüm var.` : ""}`;
}

export function gorunenEtiketler(adet: number, x: (sira: number) => number): Set<number> {
  const gorunen = new Set<number>();
  let sonKonum = -Infinity;
  for (let sira = 0; sira < adet; sira++) {
    const konum = x(sira);
    if (sira === adet - 1 || (konum - sonKonum >= 58 && (adet <= 1 || x(adet - 1) - konum >= 58))) {
      gorunen.add(sira);
      sonKonum = konum;
    }
  }
  return gorunen;
}
